import React from "react";
import {
  FUNDING,
  PayPalScriptProvider,
  BraintreePayPalButtons,
} from "@paypal/react-paypal-js";
import { BraintreeTokenizePayload } from "@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes";
import { PaymentIntentPrepareRequest } from "types/Api/Payment/paymentIntentPrepareRequest";
import IamApi from "api/IamApi";
import FundingOption from "constants/FundingOption";
import { DigitalPaymentData } from "types/digitalPaymentData";
import { IPayPalConfig } from "types/digitalPaymentConfigurations";
import { setAppLoading } from "actions/appStateActions";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedAmountOptionKey } from "selectors/paymentIntent";
import { getPayableFromSession } from "api/IamApi/entities/ezpay";
import { PayPal } from "constants/FundingOption";
import {
  getDomain,
} from "utilities/helper";
import { useNavigate } from "react-router";
import routing from "routing";
import { setPayableSession } from "actions/payableSessionActions";

export interface IPayablePayPalButtonWrapper {
  clientId: string;
  paymentIntentId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  reviewDigitalPayment: (paymentData: DigitalPaymentData) => void;
  verifyPaymentAmount: () => { isValid: boolean };
  onPrepareIntentError: (err: any, prepareRequest: PaymentIntentPrepareRequest) => void;
  config: IPayPalConfig;
  // Renders the PayPal button greyed out and unclickable (e.g. no payable option selected yet).
  disabled?: boolean;
}

// Custom component to wrap the PayPalButtons and handle currency changes
const PayablePayPalButtonWrapper = (props: IPayablePayPalButtonWrapper) => {
  const {
    amount,
    currency,
    reviewDigitalPayment,
    paymentMethod,
    verifyPaymentAmount,
    onPrepareIntentError,
    config,
    disabled = false,
  } = props;

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const selectedOptionKey = useSelector(getSelectedAmountOptionKey);

  const payPalConfig = {
    clientId: config.clientId,
    merchantAccountId: config.merchantAccountId,
    merchantId: config.merchantId,
    braintreeToken: config.braintreeToken,
    fundingSource: paymentMethod === PayPal ? FUNDING.PAYPAL : FUNDING.VENMO,
    style: { color: "gold", layout: "vertical", height: 50 },
  };

  const buttons: React.ReactNode[] = [];

  const paypalOptions = {
    clientId: payPalConfig.clientId,
    merchantId: payPalConfig.merchantId,
    dataUserIdToken: payPalConfig.braintreeToken,
    intent: "authorize",
    components: "buttons",
    currency: "USD",
    commit: false,
    enableFunding: "venmo",
  };

  // PayPal's onClick receives (data, actions); returning actions.reject() is what
  // prevents the popup from opening (there is no DOM event to preventDefault).
  const handleButtonClick = (data: any, actions: any) => {
    const isValidAmount = verifyPaymentAmount();
    if (!isValidAmount.isValid) {
      dispatch(setAppLoading(false));
      return actions.reject();
    }
    return actions.resolve();
  };
  const preparePaymentIntent = async (payload: BraintreeTokenizePayload) => {
    dispatch(setAppLoading(true));
    const prepareRequest: PaymentIntentPrepareRequest = {
      paymentAmount: amount,
      ...(selectedOptionKey ? { selectedOptionKey } : {}),
      fundingOption: FundingOption.PayPal,
      digitalWallet: {
        paymentToken: payload.nonce,
      },
    };

    await IamApi.ezpay.postPayablePrepareIntent(
      prepareRequest
    ).then(async (response) => {

      const paymentData: DigitalPaymentData = {
        payer: `${payload.details.firstName} ${payload.details.lastName}`,
        email: payload.details.email,
        mobileNumber: payload.details.phone,
        paymentToken: payload.nonce,
        paymentMethod: FundingOption.PayPal,
        serviceFee: Number(response.data.feeTransactionAmount),
      };
      reviewDigitalPayment(paymentData);
      const session = await getPayableFromSession(getDomain());
      dispatch(setPayableSession(session));
      navigate(routing.payableReview.index());
      dispatch(setAppLoading(false));
    })
      .catch((err) => {
        onPrepareIntentError(err, prepareRequest);
        dispatch(setAppLoading(false));
      });
  };

  const payPalStyle = {
    color: "gold",
    layout: "vertical",
    height: 55,
    label: "pay",
  };

  const venmoStyle = {
    color: "blue",
    layout: "vertical",
    height: 55,
    label: ''
  };


  const buttonStyle = payPalConfig.fundingSource === FUNDING.PAYPAL ? payPalStyle : venmoStyle;
  buttons.push(
    <BraintreePayPalButtons
      key={payPalConfig.fundingSource}
      /* @ts-ignore */
      style={buttonStyle}
      merchantAccountId={payPalConfig.merchantAccountId}
      forceReRender={[amount, currency, payPalConfig.style]}
      fundingSource={payPalConfig.fundingSource}
      disabled={disabled}
      createOrder={function (data, actions) {
        return actions.braintree
          .createPayment({
            flow: "checkout",
            amount: amount,
            intent: "authorize",
            currency: currency,
          })
          .then((orderId) => {
            // prepare payment intent
            return orderId;
          });
      }}
      onApprove={function (data, actions) {
        return actions.braintree.tokenizePayment(data).then((payload) => {
          // process payment intent
          preparePaymentIntent(payload);
        });
      }}
      onClick={handleButtonClick}
    />
  );

  return (
    <div style={{ height: "50px", width: "100%", alignSelf: "center" }}>
      <PayPalScriptProvider options={paypalOptions}>
        {buttons}
      </PayPalScriptProvider>
    </div>
  );
};

export default PayablePayPalButtonWrapper;